import { AnimalSexMap } from '@/model/Animal.model';
import type { Animal, AnimalApiId } from '@/model/Animal.model';
import type { AnimalDefinitionApiId, AnimalDefinitionMappingV1 } from '@/model/PlannedAnimal.model';
import type { TreatmentGroup } from '@/model/TreatmentGroup.model';
import type { CustomGridSettings } from '@/components/UI/SpreadSheet/SpreadSheet.model';
import { _isEmptyString, _isNotEmpty, _notNil } from '@/littledash';
import { renderGroupContainer } from '@/components/Studies/Create/Steps/Animals/Animals.utils';
import type Handsontable from 'handsontable';
import type HotTable from '@handsontable//react';
import type { PlannedAnimalDefinition } from '@/components/Animals/PlannedAnimals/PlannedAnimals.utils';
import type { Nullable } from '@/model/Common.model';

export interface MapAnimalRow {
  cageName: string;
  animalName: string;
  sex: 'Male' | 'Female';
  group: string;
  animalApiId: AnimalApiId;
  plannedId: string | null;
  plannedApiId: AnimalDefinitionApiId;
}

export const transformAnimalsToMapData = (
  animals: Array<Animal>,
  plannedAnimals: Array<PlannedAnimalDefinition>
): Array<MapAnimalRow> => {
  const usedPlannedIds = new Set<string>();
  const plannedAnimalsIdMap = new Map(
    plannedAnimals.map((animal) => [animal.planned_id, animal.api_id as AnimalDefinitionApiId])
  );

  return animals.map((animal) => {
    const availablePlannedId =
      (plannedAnimals ?? [])
        ?.filter(
          (plannedAnimal) =>
            plannedAnimal.sex === animal.sex && plannedAnimal.study_group_api_id === animal.study_group?.api_id
        )
        .find((plannedAnimal) => !usedPlannedIds.has(plannedAnimal.planned_id))?.planned_id ?? null;
    if (_notNil(availablePlannedId)) {
      usedPlannedIds.add(availablePlannedId);
    }
    return {
      cageName: animal.cage?.name ?? '',
      animalName: animal.name ?? '',
      sex: AnimalSexMap[animal.sex],
      group: animal.study_group?.name ?? '',
      animalApiId: animal.api_id as AnimalApiId,
      plannedId: availablePlannedId,
      plannedApiId: plannedAnimalsIdMap.get(availablePlannedId ?? '') as AnimalDefinitionApiId,
    };
  });
};

interface MapAnimalsColumnDataResult {
  columns: Array<Record<string, unknown>>;
  colHeaders: Array<string>;
  hiddenColumns: { columns: Array<number>; indicators: boolean };
}

export const mapAnimalsColumnData = (
  groups: Array<TreatmentGroup>,
  hotInstanceRef: React.RefObject<HotTable>
): MapAnimalsColumnDataResult => {
  return {
    columns: [
      {
        data: 'cageName',
        title: 'Cage Name',
        readOnly: true,
        width: 150,
      },
      {
        data: 'animalName',
        title: 'Animal Name',
        readOnly: true,
        width: 150,
      },
      {
        data: 'sex',
        title: 'Sex',
        readOnly: true,
        width: 100,
      },
      {
        data: 'group',
        title: 'Group',
        readOnly: true,
        width: 150,
        renderer: function (
          instance: Handsontable,
          td: HTMLTableCellElement,
          row: number,
          col: number,
          prop: string | number,
          value: string | null
        ): void {
          const container = renderGroupContainer(groups, value);
          td.className = 'htMiddle htDimmed';
          td.innerHTML = '';
          td.appendChild(container);
        },
      },
      {
        data: 'plannedId',
        title: 'Planned animal ID',
        type: 'dropdown',
        source: [],
        width: 150,
        allowEmpty: true,
        validator: function (this: Handsontable.CellProperties, value: string, callback: (valid: boolean) => void) {
          validateMapAnimalId(value ?? '', callback, hotInstanceRef?.current?.hotInstance ?? null, this.row, this.col);
        },
        validationErrorMessage: () => 'ID must be selected from dropdown and must be unique.',
      },
      {
        data: 'animalApiId',
        type: 'text',
        readOnly: true,
      },
    ],
    colHeaders: ['Cage Name', 'Animal Name', 'Sex', 'Group', 'Planned Animal ID'],
    hiddenColumns: { columns: [5], indicators: false },
  };
};

export const mapAnimalsSettings = (columnData: MapAnimalsColumnDataResult): CustomGridSettings => {
  return {
    ...columnData,
    contextMenu: {
      items: {
        row_above: {},
        row_below: {},
        remove_row: {},
      },
    },
    manualColumnResize: true,
    rowHeaders: false,
    outsideClickDeselects: true,
    allowInsertRow: false,
    allowInsertColumn: false,
    allowRemoveRow: false,
    minSpareRows: 0,
    columnSorting: false,
    className: 'htMiddle',
    rowHeights: 50,
    height: 'calc( 90vh - 275px )',
  };
};

export const updateCellMeta = (
  hotInstance: Handsontable,
  mapData: Array<MapAnimalRow>,
  groups: Array<TreatmentGroup>,
  plannedAnimals: Array<PlannedAnimalDefinition>
): void => {
  const idCol = hotInstance.propToCol('plannedId');

  mapData.forEach((rowData, rowIndex) => {
    const selectedGroup = groups.find((group) => group.name === rowData.group);
    const groupApiId = selectedGroup?.api_id;

    if (_notNil(groupApiId)) {
      const matchingIds = plannedAnimals
        ?.filter(
          (plannedAnimal) =>
            plannedAnimal.study_group_api_id === groupApiId && AnimalSexMap[plannedAnimal.sex] === rowData.sex
        )
        .map((animal) => animal.planned_id);

      if (_isNotEmpty(matchingIds)) {
        hotInstance.setCellMeta(rowIndex, idCol, 'source', matchingIds);
        hotInstance.setCellMeta(rowIndex, idCol, 'allowEmpty', true);
      } else {
        hotInstance.setCellMeta(rowIndex, idCol, 'readOnly', true);
      }
    }
  });
};

export const processDataChange = (
  hotInstance: Handsontable,
  plannedAnimals: Array<PlannedAnimalDefinition>
): Array<MapAnimalRow> => {
  const currentData = hotInstance.getSourceData() as Array<MapAnimalRow>;
  return currentData.map((row) => ({
    ...row,
    plannedApiId: plannedAnimals.find((animal) => animal.planned_id === row.plannedId)?.api_id as AnimalDefinitionApiId,
  }));
};

export const createAnimalMappings = (mapData: Array<MapAnimalRow>) => {
  return mapData.reduce<Array<AnimalDefinitionMappingV1>>((acc, row) => {
    if (_notNil(row.plannedId)) {
      acc.push({
        animal_api_id: row.animalApiId,
        animal_definition_api_id: row.plannedApiId,
      });
    }
    return acc;
  }, []);
};

const validateMapAnimalId = (
  value: string,
  callback: (valid: boolean) => void,
  hot: Nullable<Handsontable>,
  row: number,
  col: number
) => {
  if (_isEmptyString(value)) {
    return callback(true);
  }
  if (_notNil(hot)) {
    const availableIds = (hot.getCellMeta(row, col)?.source ?? []) as Array<string>;
    if (!availableIds.includes(value)) {
      return callback(false);
    }
    const currentData = hot.getSourceData() as Array<MapAnimalRow>;
    const duplicateIDs = currentData.filter((row) => _notNil(value) && row.plannedId === value).length;
    return callback(duplicateIDs <= 1);
  }
  return callback(false);
};
